/**
 * Utility functions for compressing and decompressing UUIDs using base62 encoding (a-zA-Z0-9)
 */

const BASE62_CHARS =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE62_MAP = BASE62_CHARS.split("").reduce(
  (map, char, i) => {
    map[char] = BigInt(i);
    return map;
  },
  {} as { [key: string]: bigint },
);

/**
 * Converts a hex string to base62
 */
function hexToBase62(hex: string): string {
  const num = BigInt(`0x${hex}`);
  let result = "";
  let remaining = num;
  const base = BigInt(62);

  while (remaining > 0n) {
    const remainder = Number(remaining % base);
    result = BASE62_CHARS[remainder] + result;
    remaining = remaining / base;
  }

  // Ensure the output is padded to the correct length (22 chars for UUID)
  return result.padStart(22, "0");
}

/**
 * Converts a base62 string back to hex
 */
function base62ToHex(base62: string): string {
  let num = 0n;
  const base = 62n;

  for (const char of base62) {
    // TODO: fix typecasting of BASE62_MAP[char]
    num = num * base + BASE62_MAP[char]!;
  }

  // Convert to hex and pad to 32 characters (16 bytes)
  return num.toString(16).padStart(32, "0");
}

/**
 * Converts a standard UUID to a compressed base62 format
 * @param uuid Standard UUID string (e.g., '123e4567-e89b-12d3-a456-426614174000')
 * @returns Compressed base62 string (22 characters, only a-zA-Z0-9)
 */
export function compressUUID(uuid: string): string {
  // Remove dashes
  const hex = uuid.replace(/-/g, "");
  return hexToBase62(hex);
}

/**
 * Converts a compressed base62 UUID back to standard UUID format
 * @param compressed Compressed base62 string (22 characters)
 * @returns Standard UUID string
 */
export function decompressUUID(compressed: string): string {
  if (compressed.length !== 22 || !/[A-Za-z0-9]+/.test(compressed)) {
    throw new Error("Invalid compressed UUID");
  }
  const hex = base62ToHex(compressed);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// Example usage:
/*
const uuid = '123e4567-e89b-12d3-a456-426614174000';
const compressed = compressUUID(uuid);
console.log(compressed);  // Will output 22 characters using only a-zA-Z0-9
const decompressed = decompressUUID(compressed);
console.log(decompressed === uuid);  // true
*/
